import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Search, FileText, Settings, Link2, Globe, Check, MapPin, ClipboardList, ShoppingCart, ArrowLeftRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';
import { getPaths } from '../utils/paths';

export default function Diensten() {
  const { language } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const paths = getPaths(language);

  const services = [
    {
      id: 'seo-strategie',
      icon: <Search className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Strategie' : 'SEO Strategy',
      badge: language === 'nl' ? 'Populair' : 'Popular',
      description: language === 'nl'
        ? 'Effectieve SEO-strategieën die daadwerkelijk werken. Data-gedreven en resultaatgericht, afgestemd op jouw business doelen.'
        : 'Effective SEO strategies that actually work. Data-driven and result-oriented, tailored to your business goals.',
      highlights: language === 'nl'
        ? ['Keyword research & strategie', 'Concurrentieanalyse', 'ROI-gedreven aanpak']
        : ['Keyword research & strategy', 'Competitor analysis', 'ROI-driven approach']
    },
    {
      id: 'seo-tekstschrijver',
      icon: <FileText className="h-6 w-6" />,
      title: language === 'nl' ? 'SEO Teksten' : 'SEO Copywriting',
      description: language === 'nl'
        ? 'SEO-teksten die aansluiten bij de zoekintentie van jouw doelgroep en converteren. Van landingspagina\'s tot blogartikelen.'
        : 'SEO copy that matches your target audience\'s search intent and converts. From landing pages to blog articles.',
      highlights: language === 'nl'
        ? ['Zoekintentie-gedreven content', 'Content gap analyse', 'Conversie-geoptimaliseerd']
        : ['Search intent driven content', 'Content gap analysis', 'Conversion optimized']
    },
    {
      id: 'technische-seo',
      icon: <Settings className="h-6 w-6" />,
      title: language === 'nl' ? 'Technische SEO' : 'Technical SEO',
      description: language === 'nl'
        ? 'Diepgaande technische audits die elke stap concreet en uitvoerbaar maken. Van Core Web Vitals tot crawlability.'
        : 'In-depth technical audits that are actionable every step of the way. From Core Web Vitals to crawlability.',
      highlights: language === 'nl'
        ? ['Core Web Vitals', 'Site architectuur', 'Crawl & indexatie']
        : ['Core Web Vitals', 'Site architecture', 'Crawl & indexation']
    },
    {
      id: 'linkbuilding',
      icon: <Link2 className="h-6 w-6" />,
      title: language === 'nl' ? 'Linkbuilding' : 'Link Building',
      description: language === 'nl'
        ? 'Een realistische linkbuilding strategie op basis van concurrentieanalyse, met een helder budget om te ranken op jouw producten en diensten.'
        : 'A realistic link building strategy based on competitor analysis, with a clear budget to rank for your products and services.',
      highlights: language === 'nl'
        ? ['Concurrentie backlink analyse', 'Digital PR', 'Kwalitatieve links']
        : ['Competitor backlink analysis', 'Digital PR', 'Quality links']
    },
    {
      id: 'seo-consultancy',
      icon: <Globe className="h-6 w-6" />,
      title: 'SEO Consultancy',
      description: language === 'nl'
        ? 'Heb je al een SEO-strategie en wil je een second opinion? Ik kijk graag mee en adviseer op strategisch niveau.'
        : 'Already have an SEO strategy and want a second opinion? I am happy to review and advise at a strategic level.',
      highlights: language === 'nl'
        ? ['Strategisch advies', 'Second opinion', 'Roadmap review']
        : ['Strategic advice', 'Second opinion', 'Roadmap review']
    },
    {
      id: 'geo',
      icon: <Globe className="h-6 w-6" />,
      title: 'GEO',
      badge: 'Nieuw',
      description: language === 'nl'
        ? 'Vindbaar worden in AI-driven zoekresultaten. De volgende stap in zoekmachineoptimalisatie met focus op LLM-zichtbaarheid.'
        : 'Get found in AI-driven search results. The next step in search engine optimization with focus on LLM visibility.',
      highlights: language === 'nl'
        ? ['AI-zoekresultaten', 'LLM-zichtbaarheid', 'Toekomstbestendig']
        : ['AI search results', 'LLM visibility', 'Future-proof']
    },
    {
      id: 'local-seo',
      icon: <MapPin className="h-6 w-6" />,
      title: language === 'nl' ? 'Lokale SEO' : 'Local SEO',
      description: language === 'nl'
        ? 'Word de nummer één in je regio. Google Business Profile optimalisatie en lokale vindbaarheid voor bedrijven met een fysieke locatie.'
        : 'Become number one in your region. Google Business Profile optimization and local visibility for businesses with a physical location.',
      highlights: language === 'nl'
        ? ['Google Business Profile', 'Lokale zoekwoorden', 'NAP consistentie']
        : ['Google Business Profile', 'Local keywords', 'NAP consistency']
    },
    {
      id: 'seo-audits',
      icon: <ClipboardList className="h-6 w-6" />,
      title: 'SEO Audits',
      description: language === 'nl'
        ? 'Een diepgaande analyse van je website op technisch, content en autoriteit vlak. Concreet, uitvoerbaar en geprioriteerd.'
        : 'An in-depth analysis of your website on technical, content and authority level. Concrete, actionable and prioritized.',
      highlights: language === 'nl'
        ? ['Technische analyse', 'Content audit', 'Actieplan met prioriteiten']
        : ['Technical analysis', 'Content audit', 'Action plan with priorities']
    },
    {
      id: 'ecommerce-seo',
      icon: <ShoppingCart className="h-6 w-6" />,
      title: 'Ecommerce SEO',
      description: language === 'nl'
        ? 'Meer organisch verkeer naar je webshop. Van categoriepagina\'s tot productpagina\'s — geoptimaliseerd voor zoekintentie én conversie.'
        : 'More organic traffic to your webshop. From category pages to product pages — optimized for search intent and conversion.',
      highlights: language === 'nl'
        ? ['Categorie optimalisatie', 'Product SEO', 'Conversie-gedreven']
        : ['Category optimization', 'Product SEO', 'Conversion-driven']
    },
    {
      id: 'migratie',
      icon: <ArrowLeftRight className="h-6 w-6" />,
      title: language === 'nl' ? 'Migraties' : 'Migrations',
      description: language === 'nl'
        ? 'Veilige site migraties zonder verlies van rankings of traffic. Van planning tot uitvoering en post-migratie monitoring.'
        : 'Safe site migrations without losing rankings or traffic. From planning to execution and post-migration monitoring.',
      highlights: language === 'nl'
        ? ['Redirect mapping', 'Pre-migratie audit', 'Post-migratie monitoring']
        : ['Redirect mapping', 'Pre-migration audit', 'Post-migration monitoring']
    },
    {
      id: 'traffic-recovery-audit',
      icon: <Check className="h-6 w-6" />,
      title: language === 'nl' ? 'Traffic Recovery Audit' : 'Traffic Recovery Audit',
      description: language === 'nl'
        ? 'Heeft je website een daling meegemaakt na een Google-update? Ik analyseer de oorzaak en stel een herstelplan op om je posities terug te winnen.'
        : 'Has your website experienced a drop after a Google update? I analyse the cause and create a recovery plan to regain your positions.',
      highlights: language === 'nl'
        ? ['Google update analyse', 'Herstelstrategie', 'Prioriteitenlijst']
        : ['Google update analysis', 'Recovery strategy', 'Priority action list']
    },
    {
      id: 'content-performance-audit',
      icon: <ClipboardList className="h-6 w-6" />,
      title: language === 'nl' ? 'Content Performance Audit' : 'Content Performance Audit',
      description: language === 'nl'
        ? 'Analyse van je bestaande content: wat presteert, wat kan weg en waar liggen onbenutte kansen. De basis voor een effectieve contentstrategie.'
        : 'Analysis of your existing content: what performs, what can go and where are untapped opportunities. The foundation for an effective content strategy.',
      highlights: language === 'nl'
        ? ['Content gap analyse', 'Pruning advies', 'Kansen identificatie']
        : ['Content gap analysis', 'Pruning advice', 'Opportunity identification']
    },
    {
      id: 'full-scope-audit',
      icon: <Globe className="h-6 w-6" />,
      title: language === 'nl' ? 'Full Scope Audit' : 'Full Scope Audit',
      description: language === 'nl'
        ? 'Een complete SEO-audit op alle 3 de pijlers: techniek, content en autoriteit. Je ontvangt een concreet rapport met geprioriteerde aanbevelingen.'
        : 'A complete SEO audit across all 3 pillars: technical, content and authority. You receive a concrete report with prioritised recommendations.',
      highlights: language === 'nl'
        ? ['Techniek + Content + Autoriteit', 'Concreet actieplan', 'Geprioriteerde aanbevelingen']
        : ['Technical + Content + Authority', 'Concrete action plan', 'Prioritised recommendations']
    }
  ];

  return (
    <div data-testid="diensten-page">
      <SEOHead
        title={language === 'nl' ? 'SEO Diensten | Technische SEO, Content & Linkbuilding' : 'Dutch SEO services | Technical SEO, content & link building for NL'}
        description={language === 'nl'
          ? 'Bekijk alle SEO-diensten van Kesult: SEO-strategie, technische SEO, linkbuilding, content, audits en meer. Vraag een vrijblijvend voorstel aan.'
          : 'All SEO services for the Dutch market: strategy, technical localisation, Dutch content, link building, audits and more. Request a free proposal.'}
        canonical="https://www.kesult.com/diensten"
        lang={language}
      />
      {/* Hero - Dark angled section */}
      <section className="relative bg-[#0B1A2E] text-white overflow-hidden" data-testid="diensten-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 lg:pb-40 relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-5">
              {language === 'nl' ? 'Diensten' : 'Services'}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight mb-6" data-testid="diensten-title">
              {language === 'nl'
                ? 'Freelance SEO Diensten'
                : 'Freelance SEO Specialist Services'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
              {language === 'nl'
                ? 'Als freelance SEO specialist is mijn doel om je te voorzien van een volledig op maat gemaakte, data-gedreven strategie die jouw organische zichtbaarheid daadwerkelijk laat groeien.'
                : 'As a freelance SEO specialist, my goal is to provide you with a fully bespoke, data-driven strategy that will actually benefit and grow your organic visibility.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`${basePath}/contact`}>
                <button className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2" data-testid="diensten-cta-primary">
                  {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to={paths.werkwijze}>
                <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2" data-testid="diensten-cta-secondary">
                  {language === 'nl' ? 'Bekijk werkwijze' : 'View process'}
                </button>
              </Link>
            </div>
          </div>
        </div>
        {/* Angled bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      <Breadcrumbs />

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to={paths.service(service.id)}
                className="group p-8 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#4286F4]/20 transition-all duration-300 flex flex-col"
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 bg-[#4286F4]/10 rounded-xl flex items-center justify-center text-[#4286F4]">
                    {service.icon}
                  </div>
                  {service.badge && (
                    <span className="bg-[#4286F4] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {service.badge}
                    </span>
                  )}
                </div>

                <h3 className="text-xl font-bold text-[#08080A] group-hover:text-[#4286F4] transition-colors mb-3">
                  {service.title}
                </h3>
                <p className="text-[#555] text-sm leading-relaxed mb-6 flex-grow">
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.highlights.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#333]">
                      <Check className="h-3.5 w-3.5 text-[#4286F4] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <span className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm group-hover:gap-3 transition-all mt-auto">
                  {language === 'nl' ? 'Meer info' : 'Find out more'}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Mijn aanpak' : 'My Approach'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
              {language === 'nl' ? 'Op maat gemaakte, kosteneffectieve SEO' : 'Bespoke, Cost-Effective SEO'}
            </h2>
            <p className="text-[#555] leading-relaxed mb-4">
              {language === 'nl'
                ? 'Ik begrijp dat ROI voor elk bedrijf essentieel is. Mijn aanpak stelt mij in staat om de overhead laag te houden, waardoor ik SEO-diensten kan aanbieden tegen een fractie van de kosten van een groter bureau.'
                : 'I understand that ROI is everything for any business. My approach allows me to keep overheads low, offering SEO services at a fraction of the cost of a larger agency.'}
            </p>
            <p className="text-[#555] leading-relaxed mb-10">
              {language === 'nl'
                ? 'Transparante pakketten met een vast maandbedrag, gebaseerd op strategische sprints van 3 maanden. Je weet precies waar je aan toe bent — geen verrassingen achteraf.'
                : 'Transparent packages with a fixed monthly fee, based on strategic 3-month sprints for your Dutch market entry. You know exactly what to expect — no surprises.'}
            </p>
            <Link to={paths.werkwijze} className="inline-flex items-center gap-2 text-[#4286F4] font-semibold text-sm hover:underline">
              {language === 'nl' ? 'Lees meer over mijn werkwijze' : 'Read more about my process'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Niet zeker welke dienst je nodig hebt?' : 'Not sure which service you need?'}
          </h2>
          <div className="flex gap-4">
            <Link to={`${basePath}/contact`}>
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a href="mailto:info@kesult.com">
              <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                {language === 'nl' ? 'Mail me' : 'Email me'}
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
